import { Box, Typography, IconButton, Stack, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Atividade } from '@azoupvendas/shared';

// AIDEV-NOTE: Componente reutilizavel para exibir lista de atividades no modal e na pagina de Atividades

const TIPO_CONFIG = {
  ligacao: { label: '📞 Ligação', color: '#1976d2' },
  email: { label: '✉️ E-mail', color: '#2e7d32' },
  tarefa: { label: '✅ Tarefa', color: '#ed6c02' },
  reuniao: { label: '🤝 Reunião', color: '#9c27b0' }
};

function formatarHorario(dataPrevista: string): string | null {
  const dt = new Date(dataPrevista);
  const hh = dt.getHours();
  const min = dt.getMinutes();
  if (hh === 0 && min === 0) return null;
  // AIDEV-NOTE: toLocaleTimeString converte UTC → local e formata com minutos sempre
  return dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

interface AtividadesListProps {
  atividades: Atividade[];
  onComplete?: (id: string) => void;
  onEdit?: (atividade: Atividade) => void;
  onDelete?: (id: string) => void;
  maxItems?: number;
  onVerTodas?: () => void;
}

export function AtividadesList({
  atividades,
  onComplete,
  onEdit,
  onDelete,
  maxItems,
  onVerTodas
}: AtividadesListProps) {
  const displayed = maxItems ? atividades.slice(0, maxItems) : atividades;
  const hasMore = maxItems && atividades.length > maxItems;

  if (atividades.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Nenhuma atividade registrada
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {displayed.map((ativ) => {
        const config = TIPO_CONFIG[ativ.tipo];
        const isRealizada = ativ.status === 'realizada';
        const horario = ativ.data_prevista ? formatarHorario(ativ.data_prevista) : null;
        return (
          <Box
            key={ativ.id}
            sx={{
              p: 1.5,
              bgcolor: isRealizada ? 'grey.50' : 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: isRealizada ? 'grey.200' : 'divider',
              opacity: isRealizada ? 0.7 : 1
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1} minWidth={0}>
                <Typography variant="caption" fontWeight={600} sx={{ color: config.color }}>
                  {config.label}
                </Typography>

                {ativ.cliente_razao_social && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    🏢 {ativ.cliente_razao_social}
                  </Typography>
                )}

                {ativ.descricao && (
                  <Typography variant="body2" mt={0.25}>
                    {ativ.descricao}
                  </Typography>
                )}

                {ativ.data_prevista && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                    {isRealizada
                      ? '✓ Realizada'
                      : `📅 ${new Date(ativ.data_prevista).toLocaleDateString('pt-BR')}${horario ? ` às ${horario}` : ''}`}
                  </Typography>
                )}
              </Box>

              <Box display="flex" alignItems="center" gap={0.25} ml={1} flexShrink={0}>
                {!isRealizada && onComplete && (
                  <IconButton
                    size="small"
                    onClick={() => onComplete(ativ.id)}
                    aria-label="Marcar como realizada"
                    title="Marcar como realizada"
                  >
                    <CheckCircleOutlineIcon fontSize="small" />
                  </IconButton>
                )}
                {onEdit && (
                  <IconButton
                    size="small"
                    onClick={() => onEdit(ativ)}
                    aria-label="Editar atividade"
                    title="Editar"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton
                    size="small"
                    onClick={() => onDelete(ativ.id)}
                    aria-label="Excluir atividade"
                    title="Excluir"
                    color="error"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
      {hasMore && onVerTodas && (
        <Button size="small" onClick={onVerTodas} sx={{ alignSelf: 'center' }}>
          Ver todas ({atividades.length}) →
        </Button>
      )}
    </Stack>
  );
}
